<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import { reactive } from "vue";
import Modal from "@/Components/Modal.vue";
import Button from "@/Components/Button.vue";
import { Head, Link } from "@inertiajs/vue3";
import InstagramIcon from "@/Components/assets/InstagramIcon.vue";
import FacebookIcon from "@/Components/assets/FacebookIcon.vue";
import PartnersShowcase from "@/Components/PartnersShowcase.vue";
import YoutubeIcon from "@/Components/assets/YoutubeIcon.vue";

// Update the approval link when the real PDF URL is available
const approvalLink = "/files/Libyan_Automobile_And_Touring_Club_Decision_To_Adopt_The_RBR_System.pdf"; // TODO: replace with the real PDF URL when available

// Testimonials data - add/remove testimonials by editing this array
const testimonials = [
    {
        quote: "RBR is a smooth system that gives instant results, lets you replay your path, and shows errors right on the screen — features you won't find anywhere else. We organize rallies frequently, and using RBR has been an outstanding choice. To be honest, it's the best system we have worked with, and truly one of the best available today.",
        author: "Ahmad Hamo",
        links: {
            instagram: "https://www.instagram.com/ahmad_hamorabi_/",
            facebook: "https://www.facebook.com/ahmedmomed.hamo",
        },
    },
    {
        quote: "I'm impressed by both the quality of the work and the speed of your responses. Great job!",
        author: "Vincent Biau",
        links: {
            instagram: "https://www.instagram.com/vincent_biau/",
        },
    },
    {
        quote: "I tried your app and it's AMAZING! … I think your app is a real game changer!",
        author: "Tiziano Internò",
        links: {
            instagram: "https://www.instagram.com/rally.pov/",
            facebook: "https://fb.me/rally.pov/",
            youtube: "https://www.youtube.com/channel/UCOvkNeuL9jConNHmF3JHgnQ",
        },
    },
    {
        quote: "RBR was at the required level and this is the system that you can rely on in organizing rallies.",
        author: "Walid Al-Jalah",
        org: "Libya Rally",
        website: "https://www.libya-rally.ly",
        links: {
            facebook: "https://www.facebook.com/p/Libya-Rally-II-61560301795045/",
        },
    },
    {
        quote: "It's always a pleasure to organise events with RoadbookRally.",
        author: "Andre Säre",
        links: {
            instagram: "https://www.rallyrampage.com/",
        },
    },
    {
        quote: "I have to admit that RoadbookRally.com is top notch solution.",
        author: "Siim Säre",
        links: {
            instagram: "https://www.instagram.com/siimsare",
        },
    },
    {
        quote: "The track/roadbook validator is outstanding! It provides immediate answers to issues, and I'm amazed at how effective and helpful it is!",
        author: "Jam Bolenza",
    },
    {
        quote: "I must admit, the RBR platform is incredible. Every time I use it, I'm amazed at how feature-rich it is and how it delivers exactly what's needed. I always feel like 'the world needs to know!' 😂",
        author: "Eugene Beetge",
        org: "Rally Rampage",
        website: "https://www.rallyrampage.com/",
    },
    {
        quote: "Well done to you for your support and for creating such an amazing platform. Myself and the rest of Rampage are really looking forward to what's coming next.",
        author: "Eugene Beetge",
        org: "Rally Rampage",
        website: "https://www.rallyrampage.com/",
    },
    {
        quote: "This is one sweet piece of software you've developed here.",
        author: "Eugene Beetge",
        org: "Rally Rampage",
        website: "https://www.rallyrampage.com/",
    },
    {
        quote: "I love the RBR software – well done😊",
        author: "Martin Camp",
        links: {
            instagram: "https://www.instagram.com/dreamsanddust_/",
            facebook: "https://www.facebook.com/profile.php?id=61574675746035",
        },
    },
    {
        quote: 'I live in South Africa and I am on the "Road to Dakar" for January 2026. As we all know navigation is one of the most critical parts of completing the Dakar. I am very grateful to be able be using the RBR software as it emulates the real world of Dakar navigation. The product is easy to use to both create routes as well as to use it as a racer, pls continue the great development as it truly does benefit the world of off-road Road Book racing - we love the RBR product and how it helps to train us on our way to compete in the Dakar',
        author: "Martin Camp",
        links: {
            instagram: "https://www.instagram.com/dreamsanddust_/",
            facebook: "https://www.facebook.com/profile.php?id=61574675746035",
        },
    },
];

const props = defineProps({
    events_showcase: Array,
});

const myImageModalProps = reactive({
    modalImageSrc: "",
    showModal: false,
});

function showImageModal(src) {
    myImageModalProps.modalImageSrc = src;
    myImageModalProps.showModal = true;
}

function closeImageModal() {
    myImageModalProps.showModal = false;
}

// setTimeout(() => {
//     throw "myError";
// }, 3000);
</script>

<template>
    <AppLayout
        title="Navigation event management platform. Organize and navigate your rallies and roadbooks with ease."
    >
        <Head>
            <link
                rel="canonical"
                :href="route('landing')"
            />
        </Head>
        <!-- Main Title -->
        <div class="mb-6 py-8 text-center">
            <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
                <span
                    class="block xl:inline"
                    style="color: #1a56db"
                    >RoadbookRally.com</span
                >
            </h1>
            <h3 class="pt-1 text-sm uppercase sm:pt-2 sm:text-xl">Navigation events - Roadbooks - GPX</h3>
            <p class="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
                Digital navigation and timing platform for roadbook and GPX rallies.<br />
                One unified system — from solo training rides to full-scale competitions.
            </p>
        </div>

        <!-- Three Hero Blocks -->
        <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-10">
            <div class="grid gap-8 lg:grid-cols-3">
                <!-- Event Participants Block -->
                <div class="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-8 shadow-lg">
                    <div class="text-center">
                        <div
                            class="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-blue-600"
                        >
                            <svg
                                class="size-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M13 10V3L4 14h7v7l9-11h-7z"
                                ></path>
                            </svg>
                        </div>
                        <h2 class="mb-4 text-2xl font-bold text-gray-900">For Event Participants</h2>
                        <p class="mb-6 text-lg text-gray-700">Join navigation events</p>

                        <div class="mb-8 space-y-3 text-left text-sm">
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Digital roadbook or GPX navigation</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Real-time tracking and instant results</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Synced route replays after every stage</span>
                            </div>

                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">... and more</span>
                            </div>
                        </div>

                        <Button
                            href="/events"
                            color="primary"
                            variant="solid"
                            class="w-full rounded-md border border-transparent px-6 py-3 text-base font-medium md:text-lg"
                        >
                            Explore events
                        </Button>
                        <div class="mt-3 text-left">
                            <a
                                href="/faq/how-to-participate-in-the-event-25"
                                class="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                            >
                                How it works
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Individual Rides Block -->
                <div class="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-8 shadow-lg">
                    <div class="text-center">
                        <div
                            class="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-blue-600"
                        >
                            <svg
                                class="size-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 9m0 8V9m0 0V7"
                                ></path>
                            </svg>
                        </div>
                        <h2 class="mb-4 text-2xl font-bold text-gray-900">For Individual Rides</h2>
                        <p class="mb-6 text-lg text-gray-700">
                            Discover, ride, and share roadbooks or GPX trails with riders worldwide.
                        </p>
                        <div class="mb-8 space-y-3 text-left text-sm">
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Manage your own routes in one place</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Buy or sell roadbooks and GPX trails easily</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Replay and analyze your rides</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">... and more</span>
                            </div>
                        </div>
                        <div class="space-y-2">
                            <Button
                                href="/tracks"
                                color="primary"
                                variant="solid"
                                class="w-full rounded-md border border-transparent px-6 py-3 text-base font-medium md:text-lg"
                            >
                                Ride now
                            </Button>
                            <div class="mt-3 text-left">
                                <a
                                    href="/faq/how-does-it-work-for-individual-riders-75"
                                    class="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                    How it works
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Event Organizers Block -->
                <div class="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-8 shadow-lg">
                    <div class="text-center">
                        <div
                            class="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-blue-600"
                        >
                            <svg
                                class="size-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                ></path>
                            </svg>
                        </div>
                        <h2 class="mb-4 text-2xl font-bold text-gray-900">For Event Organizers</h2>
                        <p class="mb-6 text-lg text-gray-700">
                            Your one-stop solution for organizing and managing navigation events of any size
                        </p>
                        <div class="mb-8 space-y-3 text-left text-sm">
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Live tracking & instant results</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Competitor & registration management</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Secure roadbook delivery & timing</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">Automatic penalties & speed zones</span>
                            </div>
                            <div class="flex items-start">
                                <svg
                                    class="mr-2 mt-0.5 size-5 shrink-0 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <span class="text-gray-700">... and more</span>
                            </div>
                        </div>
                        <Button
                            href="/my-events"
                            color="primary"
                            variant="solid"
                            class="w-full rounded-md border border-transparent px-6 py-3 text-base font-medium md:text-lg"
                        >
                            Start organizing
                        </Button>
                        <div class="mt-3 text-left">
                            <a
                                href="/faq/how-do-i-organize-my-first-event-63"
                                class="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                            >
                                How to organize?
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative py-2 pb-12 pt-20">
            <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div class="mx-3 overflow-hidden bg-white shadow-xl sm:rounded-lg">
                    <div class="mx-8 flex flex-col justify-items-center space-y-4 pt-8 sm:hidden">
                        <div>
                            <a
                                href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=6380,6394,6430,6342,6428,6412,6368,6414,6386,6438,6366,6390,6408,6418,6459,6444,6450,6314,6354,6370,6340,6372,6382,6384,6436|6438"
                            >
                                <video
                                    src="/img/landing-replay-preview-video.webm"
                                    muted=""
                                    autoplay=""
                                    loop=""
                                    loading="lazy"
                                    class="rounded border bg-white p-1 shadow-lg"
                                ></video>
                            </a>
                        </div>
                        <div class="pb-6 text-center text-gray-800">
                            <a
                                href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=6380,6394,6430,6342,6428,6412,6368,6414,6386,6438,6366,6390,6408,6418,6459,6444,6450,6314,6354,6370,6340,6372,6382,6384,6436|6438"
                                >Event result synced replay
                            </a>
                        </div>
                        <div>
                            <a href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=|6319">
                                <img
                                    src="/img/landing/detailed-penalties-report.png"
                                    alt="Detailed penalties report on RoadbookRally.com"
                                    class="rounded border bg-white p-1 shadow-lg"
                                />
                            </a>
                        </div>
                        <div class="pb-6 text-center text-gray-800">
                            <a href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=|6319"
                                >Detailed penalties report
                            </a>
                        </div>
                        <div>
                            <img
                                src="/img/landing/event-live-tracking.jpg"
                                alt="RoadbookRally.com Event Live Tracking Preview"
                                class="rounded border bg-white p-1 shadow-lg"
                                @click="showImageModal($event.target.src)"
                            />
                        </div>
                        <div class="pb-6 text-center text-gray-800">
                            <span
                                class="cursor-pointer font-medium text-blue-600 hover:underline"
                                @click="showImageModal('/img/landing/event-live-tracking.jpg')"
                                >Event Live Tracking</span
                            >
                        </div>
                        <div>
                            <img
                                src="/img/landing/track-validator-preview.png"
                                alt="RoadbookRally.com Track owner preview and structure validation"
                                class="rounded border bg-white p-1 shadow-lg"
                                @click="showImageModal($event.target.src)"
                            />
                        </div>
                        <div class="pb-6 text-center text-gray-800">
                            <span
                                class="cursor-pointer font-medium text-blue-600 hover:underline"
                                @click="showImageModal('/img/landing/track-validator-preview.png')"
                                >Track owner preview and structure validation</span
                            >
                        </div>
                        <div>
                            <img
                                src="/img/faq/event-edit-penalties-preview.png?v=2"
                                alt="RoadbookRally.com Event penalties configuration"
                                class="cursor-pointer rounded border bg-white p-1 shadow-lg"
                                @click="showImageModal('/img/faq/event-edit-penalties.png?v=2')"
                            />
                        </div>
                        <div class="pb-6 text-center text-gray-800">
                            <span
                                class="cursor-pointer font-medium text-blue-600 hover:underline"
                                @click="showImageModal('/img/faq/event-edit-penalties.png?v=2')"
                                >Event penalties configuration</span
                            >
                        </div>
                    </div>
                    <div class="text-md p-8 leading-7">
                        <div
                            class="float-right mb-6 ml-3 hidden w-2/5 grid-cols-1 justify-items-center space-y-4 sm:grid md:ml-6"
                        >
                            <div>
                                <a
                                    href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=6380,6394,6430,6342,6428,6412,6368,6414,6386,6438,6366,6390,6408,6418,6459,6444,6450,6314,6354,6370,6340,6372,6382,6384,6436|6438"
                                >
                                    <video
                                        src="/img/landing-replay-preview-video.webm"
                                        muted=""
                                        autoplay=""
                                        loop=""
                                        loading="lazy"
                                        class="rounded border bg-white p-1 shadow-lg"
                                    ></video>
                                </a>
                            </div>
                            <div class="pb-6 text-center text-gray-800">
                                <a
                                    href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=6380,6394,6430,6342,6428,6412,6368,6414,6386,6438,6366,6390,6408,6418,6459,6444,6450,6314,6354,6370,6340,6372,6382,6384,6436|6438"
                                    >Event result synced replay
                                </a>
                            </div>
                            <div>
                                <a href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=|6319">
                                    <img
                                        src="/img/landing/detailed-penalties-report.png"
                                        alt="Detailed penalties report on RoadbookRally.com"
                                        class="rounded border bg-white p-1 shadow-lg"
                                    />
                                </a>
                            </div>
                            <div class="pb-6 text-center text-gray-800">
                                <a href="/event/2PR7G/alakonnu-ralli-2023/replay#ccs=|6319"
                                    >Detailed penalties report
                                </a>
                            </div>
                            <div>
                                <a href="/img/landing/event-live-tracking.jpg">
                                    <img
                                        src="/img/landing/event-live-tracking.jpg"
                                        alt="RoadbookRally.com Event Live Tracking Preview"
                                        class="rounded border bg-white p-1 shadow-lg"
                                    />
                                </a>
                            </div>
                            <div class="pb-6 text-center text-gray-800">
                                <a href="/img/landing/event-live-tracking.jpg">Event Live Tracking</a>
                            </div>
                            <div>
                                <img
                                    src="/img/landing/track-validator-preview.png"
                                    alt="RoadbookRally.com Track owner preview and structure validation"
                                    class="cursor-pointer rounded border bg-white p-1 shadow-lg"
                                    @click="showImageModal($event.target.src)"
                                />
                            </div>
                            <div class="pb-6 text-center text-gray-800">
                                <span
                                    class="cursor-pointer font-medium text-blue-600 hover:underline"
                                    @click="showImageModal('/img/landing/track-validator-preview.png')"
                                    >Track owner preview and structure validation</span
                                >
                            </div>
                            <div>
                                <img
                                    src="/img/faq/event-edit-penalties-preview.png?v=2"
                                    alt="RoadbookRally.com Event penalties configuration"
                                    class="cursor-pointer rounded border bg-white p-1 shadow-lg"
                                    @click="showImageModal('/img/faq/event-edit-penalties.png?v=2')"
                                />
                            </div>
                            <div class="pb-6 text-center text-gray-800">
                                <span
                                    class="cursor-pointer font-medium text-blue-600 hover:underline"
                                    @click="showImageModal('/img/faq/event-edit-penalties.png?v=2')"
                                    >Event penalties configuration</span
                                >
                            </div>
                        </div>
                        <h3>Organize navigation rallies without the chaos</h3>
                        <br />
                        <h3 class="text-base font-normal">
                            Run professional-grade events from one platform — tracked, scored, rewatched, and
                            remembered. No more Excel sheets, WhatsApp groups, or missed checkpoints.
                        </h3>
                    </div>

                    <hr />
                    <br />

                    <div class="p-4">
                        <section>
                            <div class="ml-4 space-y-4">
                                <!-- Feature Blocks -->
                                <div class="space-y-10 sm:space-y-12">
                                    <!-- Centralized Event Management -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Centralized Event Management
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Plan and control your entire rally — classes, stages, riders,
                                            results, and penalties — all in one dashboard.
                                            <span class="font-medium text-neutral-900"
                                                >Run your event from a single place and eliminate messy
                                                coordination.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Live Tracking & Instant Results -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Live Tracking &amp; Instant Results
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Track competitors in real time with automatic scoring and penalties.
                                            <span class="font-medium text-neutral-900"
                                                >Keep your event transparent, accurate, and exciting from start
                                                to finish.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Secure Track Delivery -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Secure Track Delivery
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Distribute roadbooks and GPX files automatically to verified riders
                                            — only when it’s time to start.
                                            <span class="font-medium text-neutral-900"
                                                >Protect your routes and prevent early access or leaks.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Automatic Speed & Zone Detection -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Automatic Speed &amp; Zone Detection
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Speed limits, neutralizations, and transfer zones are recognized
                                            from your roadbook and applied automatically.
                                            <span class="font-medium text-neutral-900"
                                                >Ensure safety and fairness without manual checks.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Multi-Class & Multi-Stage Support -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Multi-Class &amp; Multi-Section Support
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Handle multiple classes and selective sections with ease.
                                            <span class="font-medium text-neutral-900"
                                                >Scale from small club rides to full multi-day rallies
                                                effortlessly.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Rolling Starts -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Rolling Starts
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Start riders automatically, without needing a referee or manual
                                            timing.
                                            <span class="font-medium text-neutral-900"
                                                >Simplify logistics and keep your start schedule flexible.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Late Track Updates -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Late Track Updates
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Upload route corrections even while the event is running — riders
                                            receive them instantly.
                                            <span class="font-medium text-neutral-900"
                                                >Keep everyone synced and results accurate with zero
                                                downtime.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Track Validation -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Track Validation
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Roadbook structure is checked automatically.
                                            <span class="font-medium text-neutral-900"
                                                >Prevent errors and ensure clean data.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Replay & Comparison -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Replay &amp; Comparison
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Replay rides, compare performances, and review navigation accuracy.
                                            <span class="font-medium text-neutral-900"
                                                >Deliver professional-grade post-event analysis and
                                                storytelling.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- No Extra Hardware -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            No Extra Hardware
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Riders use their own Android devices for GPS tracking and
                                            navigation.
                                            <span class="font-medium text-neutral-900"
                                                >Reduce costs and simplify setup — no special trackers
                                                required.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Offline-Ready -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Offline-Ready
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Competitors can ride without network coverage — data syncs
                                            automatically when back online.
                                            <span class="font-medium text-neutral-900"
                                                >Reliable performance, even in remote areas.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Rally Navigator Import -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Rally Navigator Import
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            Import your OpenRally GPX files directly into the platform.
                                            <span class="font-medium text-neutral-900"
                                                >Set up new events in minutes — no reformatting needed.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- Always Evolving -->
                                    <section>
                                        <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                            Always Evolving
                                        </h3>
                                        <p class="mt-2 text-neutral-700">
                                            New features and improvements released constantly.
                                            <span class="font-medium text-neutral-900"
                                                >Your rally tools stay modern, fast, and organizer-tested.</span
                                            >
                                        </p>
                                    </section>

                                    <!-- CTA -->
                                    <footer class="pt-2">
                                        <Button
                                            href="/my-events"
                                            color="primary"
                                            variant="solid"
                                            class="flex items-center justify-center rounded-md border border-transparent px-8 py-3 text-base md:px-10 md:py-4 md:text-lg"
                                        >
                                            Create Your First Event in Minutes
                                        </Button>
                                        <p class="mt-3 text-sm text-neutral-500">
                                            Trusted by organizers who can’t afford mistakes.
                                        </p>
                                    </footer>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative py-2 pt-20">
            <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div class="mx-3 overflow-hidden bg-white shadow-xl sm:rounded-lg">
                    <div class="p-8 leading-7">
                        <h3 class="pb-4 font-bold">
                            <img
                                src="/img/rbr-app-icon.png"
                                alt="Download on Google Play store"
                                class="inline h-11 pr-2.5"
                            />
                            RBR Roadbook Reader App
                        </h3>
                        <div class="float-right ml-8 w-1/4">
                            <img
                                src="/img/manual/rb-portrait.png"
                                alt="RBR Roadbook Reader - Application"
                                class="mb-8 rounded border bg-white p-1 shadow-lg"
                                @click="showImageModal($event.target.src)"
                            />
                            <img
                                src="/img/help/navigate-by-gpx-trail-screenshot.jpg?v=2"
                                alt="RBR Roadbook Reader - Application"
                                class="rounded border bg-white p-1 shadow-lg"
                                @click="showImageModal($event.target.src)"
                            />
                        </div>

                        <h4>Digital roadbook and GPX trail navigation app for modern rally riders.</h4>
                        <br />
                        The ultimate navigation tool for riders and organizers — navigate with ease using all
                        the necessary instruments, speed zones, waypoints, direction arrows, and more.
                        Compatible with any Bluetooth remote on the market.
                        <br />
                        <br />
                        Experience seamless navigation and never miss a turn again with the
                        <strong>RBR Roadbook Reader</strong> app.
                    </div>

                    <hr />
                    <br />

                    <div class="p-8 pt-2">
                        <div class="space-y-10 sm:space-y-12">
                            <!-- Free and Accessible -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Free and Ready to Ride
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    The app is free to download and use.
                                    <span class="font-medium text-neutral-900"
                                        >Join any event or ride roadbooks without extra cost.</span
                                    >
                                </p>
                            </section>

                            <!-- Roadbook & GPX Navigation -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Roadbook &amp; GPX Navigation
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Supports digital roadbooks and GPX trails, with accurate distance, CAP, and
                                    speed indicators.
                                    <span class="font-medium text-neutral-900"
                                        >Navigate any event or track — from training rides to professional
                                        rallies.</span
                                    >
                                </p>
                            </section>

                            <!-- Waypoint & Speed Zone Aware Navigation -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Waypoint Control & Automatic Penalties
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Validate or skip waypoints directly in the app — the system tracks your
                                    actual route and applies penalties automatically.
                                    <span class="font-medium text-neutral-900"
                                        >Stay in control of navigation while the app handles fairness and
                                        scoring for you.</span
                                    >
                                </p>
                            </section>

                            <!-- Speed Zone Awareness -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Visual Cues for Speed Zone Awareness
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    The app visually alerts you to speed limits using background color changes
                                    and on-screen indicators.
                                    <span class="font-medium text-neutral-900"
                                        >Stay compliant with event speed rules and avoid penalties.</span
                                    >
                                </p>
                            </section>

                            <!-- Intelligent Autoscroll -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Intelligent <a href="/faq/what-is-the-autoscroll-37">Autoscroll</a>
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    The app automatically scrolls the roadbook as you ride — even if you don’t
                                    have an external remote connected.
                                    <span class="font-medium text-neutral-900"
                                        >Focus on safe riding, not manual scrolling.</span
                                    >
                                </p>
                            </section>

                            <!-- Full Remote Compatibility -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Full Remote Compatibility
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Works with any Bluetooth remote or device buttons for roadbook and odometer
                                    control.
                                    <span class="font-medium text-neutral-900"
                                        >Use your existing hardware — no extra setup required.</span
                                    >
                                </p>
                            </section>

                            <!-- Customizable Layout -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Customizable Layout
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Choose the number of visible roadbook rows, set your main row, and adjust
                                    display preferences.
                                    <span class="font-medium text-neutral-900"
                                        >Tailor the interface to your riding style.</span
                                    >
                                </p>
                            </section>

                            <!-- Odometer & Tripmeter Control -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Odometer &amp; Tripmeter Control
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Adjust your distance manually with a single tap
                                    <span class="font-medium text-neutral-900"
                                        >Keep navigation quick &amp; precise at all times.</span
                                    >
                                </p>
                            </section>

                            <!-- Visual Cues for Turns -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Visual Cues for Upcoming Turns (<a href="/faq/roadbook-countdown-style-38"
                                        >"countdown style"</a
                                    >)
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    The distance box changes color as you approach the next tulip (modern
                                    “countdown” DTW style).
                                    <span class="font-medium text-neutral-900"
                                        >React faster and anticipate every direction change.</span
                                    >
                                </p>
                            </section>

                            <!-- Zoom & Readability -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Zoom &amp; Readability
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Single click to view roadbook diagram or notes in full-screen mode.
                                    <span class="font-medium text-neutral-900"
                                        >See every tulip clearly — even in the toughest terrain.</span
                                    >
                                </p>
                            </section>

                            <!-- Rider Personalization -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Rider Personalization
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Adjust colors, and behavior options to match your personal preferences.
                                    <span class="font-medium text-neutral-900"
                                        >Your navigation setup — just the way you like it.</span
                                    >
                                </p>
                            </section>

                            <!-- Replay & Comparison -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Replay &amp; Comparison
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    Replay your rides, compare performances, and review navigation accuracy.
                                    <span class="font-medium text-neutral-900"
                                        >Professional-grade post-ride analysis.</span
                                    >
                                </p>
                            </section>

                            <!-- Offline-Ready -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">Offline-Ready</h3>
                                <p class="mt-2 text-neutral-700">
                                    You can ride without network coverage — data syncs automatically when back
                                    online.
                                    <span class="font-medium text-neutral-900"
                                        >Reliable performance, even in remote areas.</span
                                    >
                                </p>
                            </section>

                            <!-- Future Updates -->
                            <section>
                                <h3 class="text-lg font-semibold text-neutral-900 sm:text-xl">
                                    Constantly Improving
                                </h3>
                                <p class="mt-2 text-neutral-700">
                                    New features are regularly added based on rider and organizer feedback.
                                    <span class="font-medium text-neutral-900"
                                        >Stay ahead with an app that keeps evolving.</span
                                    >
                                </p>
                            </section>
                        </div>

                        <div class="my-6 flex items-center space-x-4">
                            <a
                                href="https://play.google.com/store/apps/details?id=com.roadbookrally.rbrr"
                                target="_blank"
                            >
                                <img
                                    src="/img/get-it-on-google-play.svg"
                                    alt="Download on Google Play store"
                                    class="h-12"
                                />
                            </a>
                            <div class="hidden leading-8 sm:block">
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.roadbookrally.rbrr"
                                    target="_blank"
                                >
                                    <img
                                        src="/img/rbr-app-icon.png"
                                        alt="Download on Google Play store"
                                        class="inline h-11"
                                    />
                                    <span class="hidden pl-2.5 sm:inline">RBR Roadbook Reader</span>
                                </a>
                            </div>
                        </div>
                        <div class="block sm:hidden">
                            <a
                                href="https://play.google.com/store/apps/details?id=com.roadbookrally.rbrr"
                                target="_blank"
                            >
                                <img
                                    src="/img/rbr-app-icon.png"
                                    alt="Download on Google Play store"
                                    class="inline h-11"
                                />
                                <span class="pl-2.5 sm:inline">RBR Roadbook Reader</span>
                            </a>
                        </div>
                        <p class="mt-3 text-sm text-neutral-500">
                            Free to use with RBR events — compatible with all major Bluetooth remotes.
                        </p>
                        <!--                        <div class="flex items-center space-x-4">
                            <a href="https://testflight.apple.com/join/AqAWJNJT">
                                <img
                                    src="/img/apple-app-store.svg"
                                    alt="Download on Apple App store"
                                    class="h-32"
                                />
                            </a>
                            <div>
                                The iOS app is in development and should only be used for testing and navigating
                                single tracks with PINs. <br />
                                <span class="text-red-500">Do not use iOS app for events</span>; it's not stable
                                yet. For event participation, use Android. <br />
                                Bluetooth buttons are not supported on iOS yet.
                            </div>
                        </div>-->
                        <!--                        Digital roadbook acts similar to the traditional one:
                        you have a roadbook and buttons to scroll the roadbook.
                        You have a trip meter and buttons to control it. Keep
                        them synced and enjoy the ride!-->
                    </div>
                </div>
            </div>
        </div>

        <section
            id="testimonials"
            aria-labelledby="testimonials-title"
            class="py-20 sm:py-24"
            data-testid="testimonials-section"
        >
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
                <div class="mx-auto max-w-3xl text-center">
                    <h2
                        id="testimonials-title"
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        What organizers and riders say
                    </h2>
                    <p class="mt-4 text-lg leading-8 text-gray-600">
                        Trusted by organizers worldwide — officially approved by the
                        <strong>Libyan Motor Federation</strong> (<a
                            :href="approvalLink"
                            target="_blank"
                            >approval letter, PDF</a
                        >).
                    </p>
                </div>

                <!-- Testimonials cards -->
                <div class="mt-8 sm:mt-12">
                    <!-- Mobile: horizontal scroll -->
                    <div
                        class="flex snap-x snap-mandatory scroll-px-6 gap-4 overflow-x-auto sm:hidden"
                        aria-label="Testimonials"
                    >
                        <div
                            v-for="(testimonial, index) in testimonials"
                            :key="index"
                            class="min-w-[85%] snap-start rounded-2xl border border-gray-200 bg-white/70 p-6 shadow-sm"
                            data-testid="testimonial-card"
                        >
                            <figure>
                                <blockquote class="text-base leading-7 text-gray-900">
                                    "{{ testimonial.quote }}"
                                </blockquote>
                                <figcaption class="mt-4">
                                    <div class="font-medium text-gray-900">{{ testimonial.author }}</div>
                                    <div
                                        v-if="testimonial.org"
                                        class="text-sm text-gray-500"
                                    >
                                        <a
                                            v-if="testimonial.website"
                                            :href="`${testimonial.website}${testimonial.website.includes('?') ? '&' : '?'}utm_source=roadbookrally.com&utm_medium=testimonial&utm_campaign=organizer_testimonials&utm_content=${encodeURIComponent(testimonial.org || testimonial.author)}`"
                                            target="_blank"
                                            class="rounded text-gray-500 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            {{ testimonial.org }}
                                        </a>
                                        <span v-else>{{ testimonial.org }}</span>
                                    </div>
                                    <div
                                        v-if="testimonial.links"
                                        class="mt-2 flex gap-3"
                                    >
                                        <a
                                            v-if="testimonial.links.instagram"
                                            :href="testimonial.links.instagram"
                                            target="_blank"
                                            class="rounded text-xs text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            <InstagramIcon></InstagramIcon>
                                        </a>
                                        <a
                                            v-if="testimonial.links.facebook"
                                            :href="testimonial.links.facebook"
                                            target="_blank"
                                            class="rounded text-xs text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            <FacebookIcon></FacebookIcon>
                                        </a>
                                        <a
                                            v-if="testimonial.links.youtube"
                                            :href="testimonial.links.youtube"
                                            target="_blank"
                                            class="rounded text-xs text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            <YoutubeIcon></YoutubeIcon>
                                        </a>
                                    </div>
                                </figcaption>
                            </figure>
                        </div>
                    </div>

                    <!-- Desktop: responsive grid -->
                    <div
                        class="hidden gap-6 sm:grid sm:grid-cols-2 xl:grid-cols-3"
                        aria-label="Testimonials"
                    >
                        <div
                            v-for="(testimonial, index) in testimonials"
                            :key="index"
                            class="rounded-2xl border border-gray-200 bg-white/70 p-6 shadow-sm"
                            data-testid="testimonial-card"
                        >
                            <figure>
                                <blockquote class="text-base leading-7 text-gray-900">
                                    "{{ testimonial.quote }}"
                                </blockquote>
                                <figcaption class="mt-4">
                                    <div class="font-medium text-gray-900">{{ testimonial.author }}</div>
                                    <div
                                        v-if="testimonial.org"
                                        class="text-sm text-gray-500"
                                    >
                                        <a
                                            v-if="testimonial.website"
                                            :href="`${testimonial.website}${testimonial.website.includes('?') ? '&' : '?'}utm_source=roadbookrally.com&utm_medium=testimonial&utm_campaign=organizer_testimonials&utm_content=${encodeURIComponent(testimonial.org || testimonial.author)}`"
                                            target="_blank"
                                            class="text-gray-500 visited:text-gray-500 hover:text-gray-500"
                                        >
                                            {{ testimonial.org }}
                                        </a>
                                        <span v-else>{{ testimonial.org }}</span>
                                    </div>
                                    <div
                                        v-if="testimonial.links"
                                        class="mt-2 flex gap-3"
                                    >
                                        <a
                                            v-if="testimonial.links.instagram"
                                            :href="testimonial.links.instagram"
                                            target="_blank"
                                        >
                                            <InstagramIcon></InstagramIcon>
                                        </a>
                                        <a
                                            v-if="testimonial.links.facebook"
                                            :href="testimonial.links.facebook"
                                            target="_blank"
                                        >
                                            <FacebookIcon></FacebookIcon>
                                        </a>
                                        <a
                                            v-if="testimonial.links.youtube"
                                            :href="testimonial.links.youtube"
                                            target="_blank"
                                            class="rounded text-xs text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            <YoutubeIcon></YoutubeIcon>
                                        </a>
                                    </div>
                                </figcaption>
                            </figure>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Partners Showcase Section -->
        <section class="py-20 sm:py-24">
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
                <PartnersShowcase
                    :partners="props.events_showcase"
                    title="Proven in real rally events"
                    subtitle="Supports Moto, Auto, Quad/ATV, SSV/UTV, 4×4 and other vehicle types"
                />
            </div>
        </section>

        <div class="relative py-2 pt-20">
            <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div class="overflow-hidden bg-white shadow-xl sm:rounded-lg">
                    <!-- Bottom right conversion links -->
                    <div class="flex justify-end p-8">
                        <div class="flex flex-col gap-3 text-right sm:flex-row sm:gap-6">
                            <Link
                                href="/faq"
                                class="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                                FAQ →
                            </Link>
                            <Link
                                href="/faq/how-do-i-organize-my-first-event-63"
                                class="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                                Organize your first event →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>

    <Modal
        :show="myImageModalProps.showModal"
        max-width="min"
        @close="closeImageModal"
    >
        <div @click="closeImageModal()">
            <img
                :src="myImageModalProps.modalImageSrc"
                class="max-h-full rounded border bg-white p-1 shadow-lg sm:max-h-screen sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg"
            />
        </div>
    </Modal>
</template>
